import clsx from 'clsx';
import { useStateWithTimeout } from '../../../hooks/useStateWithTimeout';
import { isFunction, isString } from '../../../utils/assertions';
import Button from '../../atoms/button/Button';
import { CloseCross, TickFilledSuccess } from '../../atoms/icons';
import TickFilledError from '../../atoms/icons/TickFilledError';
import Portal from '../../atoms/portal';
import { Text } from '../../typography/text/Text';
import styles from './NotificationBar.module.scss';
import { NOTIFICATION_BAR_DATA, NOTIFICATION_EXCLUDES } from './data';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import app from '@frontastic/catwalk/src/js/app/app';
import { useScrollLock } from '../../../utils/useLockScroll';
import { useNoticesContext } from '../../../provider/notices/NoticesContext';
import { useMedia } from 'react-use';
import { useCartContext } from '../../../provider/cart/CartContext';
import { formatErrorMessage } from '../forms/login/utils';

const typeStatus = {
  success: 'success',
  warning: 'warning',
  error: 'error',
  urgent: 'urgent',
  custom: 'custom',
};

const NotificationBar = ({
  code = null,
  timeout = 1000000,
  type = typeStatus.success,
  message = '',
  offsetLeft = null,
  offsetTop = '27',
  offsetRight = '84',
  offsetBottom = null,
  id,
  onClose = () => null,
}) => {
  const history = useHistory();
  const { removeNotice, notices } = useNoticesContext();
  const { showCart } = useCartContext();
  const [notification, setNotification] = useState(null);
  const isMobile = useMedia('(max-width: 767px)', false);

  const shown = false;
  const [
    showNotification,
    setShowState,
    setRenderWithTimeout,
    clearRenderTimeout,
  ] = useStateWithTimeout(true, timeout);

  const handleNotificationRemove = (id) => {
    if (notices.type) {
      localStorage.removeItem('currentNotice');
      return removeNotice();
    }
    app.getStore().dispatch({
      type: 'Frontastic.Notification.remove',
      id,
    });
  };
  useEffect(() => {
    if (showNotification !== shown) {
      setRenderWithTimeout(false);
    } else {
      handleNotificationRemove(id);
    }
  }, [showNotification]);

  useEffect(() => {
    setShowState(true);
  }, [timeout]);

  useEffect(() => {
    if (
      showCart &&
      (code === 'account.message.cartAdd' || code === 'cart.message.remove')
    ) {
      removeNotice();
      return;
    }

    const notificationObj = NOTIFICATION_BAR_DATA.find((n) => n.code === code);

    if (
      (!notificationObj && !message && !type) ||
      NOTIFICATION_EXCLUDES.includes(code)
    )
      return;

    const formatData = {
      ...notificationObj,
      message: (
        <div className={styles.errorWithLink}>
          {formatErrorMessage(notificationObj?.message || message)}
          {(notificationObj?.message || message).includes('The cart is frozen.') && (
            <Button
              shape="square"
              variant="primary"
              className={styles.buttonLink}
              as="text"
              onClick={() =>
                app
                  .getRouter()
                  .push('Frontastic.Frontend.Master.Checkout.cart')
              }
            >
              Click here to return to your cart
            </Button>
          )}
        </div>
      ),
    };
    setNotification(formatData);
  }, [showNotification]);

  const handleClick = (data) => {
    if (!data) return;
    if (isFunction(data)) {
      return;
    }
    if (isString(data)) {
      return history.push(data);
    }
  };

  const handleClose = (event) => {
    event.stopPropagation();
    clearRenderTimeout();
    setShowState(false);
    handleNotificationRemove(id);
    onClose();
  };

  const infoIcon = (notificationType) => {
    switch (notificationType) {
      case 'warning': {
        return (
          <TickFilledError
            className={styles.notificationBarInfoIcon}
            bgcolor="#B61E0A"
            color="#fff"
            width="20px"
            height="20px"
          />
        );
      }
      case 'error': {
        return (
          <TickFilledError
            className={styles.notificationBarInfoIcon}
            bgcolor="#B61E0A"
            color="#fff"
            width="20px"
            height="20px"
          />
        );
      }
      default: {
        return (
          <TickFilledSuccess
            className={styles.notificationBarInfoIcon}
            bgcolor="#22520B"
            color="#fff"
            width="20px"
            height="20px"
          />
        );
      }
    }
  };
  useScrollLock(showNotification && !!notification);

  return (
    showNotification &&
    !!notification && (
      <Portal renderInto="NotificationBar">
        <div className={styles.notificationBarBackground}>
          <div
            className={styles.notificationBarWrapper}
            style={{
              top: offsetTop,
              left: offsetLeft,
              right: offsetRight,
              bottom: offsetBottom,
            }}
            onClick={handleClose}
          >
            <div className={styles.notificationBarInfoContainer}>
              <div className={styles.notificationBarInfoTextRow}>
                {infoIcon(type)}
                <div className={styles.notificationBarInfoText}>
                  <Text
                    as="span"
                    size="xxs"
                    textTransform="none"
                    weight="normal"
                    className={clsx(
                      styles.notificationBarMessage,
                      notification?.description
                        ? styles.notificationBarInfoTextHasDesc
                        : '',
                    )}
                  >
                    {notification?.message || message}
                  </Text>
                  {notification?.description && (
                    <div className={styles.notificationBarDescription}>
                      <Text
                        as="span"
                        size="xxs"
                        textTransform="none"
                        className={styles.notificationBarInfoTextItalic}
                      >
                        {notification?.description}
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {(notification?.xsTitleButton || notification?.titleButton) && (
              <div className={styles.notificationBarButton}>
                <Button
                  variant="secondary"
                  onClick={() => handleClick(notification?.handleClickData)}
                >
                  <Text
                    as="span"
                    className={styles.notificationBarNowrap}
                    size="xs"
                    textTransform="uppercase"
                  >
                    {isMobile
                      ? notification?.xsTitleButton ?? notification?.titleButton
                      : notification?.titleButton}
                  </Text>
                </Button>
              </div>
            )}
            <CloseCross
              className={styles.notificationBarCloseIcon}
              width="13px"
              height="10px"
              onClick={handleClose}
            />
          </div>
        </div>
      </Portal>
    )
  );
};

NotificationBar.propTypes = {
  type: PropTypes.oneOf(Object.keys(typeStatus)),
  code: PropTypes.string,
  message: PropTypes.string,
  onClose: PropTypes.func,
  timeout: PropTypes.number,
  offsetLeft: PropTypes.string,
  offsetTop: PropTypes.string,
  offsetRight: PropTypes.string,
  offsetBottom: PropTypes.string,
};

export default NotificationBar;
